import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authService } from '@services/auth.service';
import { platformAuthService } from '@services/platform-auth.service';
import { authPersistStorage, clearTokens } from '@/lib/tokenStorage';
import { resetIdentityScopedState } from '@/lib/sessionReset';

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  role: string;
  tenantId: string;
  isPlatformUser?: boolean;
  platformRole?: string;
  /** Platform Main Super Admin (Admin Management). */
  isPrimary?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;
  login: (user: User, token: string) => void;
  platformLogin: (user: User, token: string) => void;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  initialize: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      _hasHydrated: false,

      login: (user, token) => {
        // Before the new identity is live: a previous session in this browser
        // may have left cached server data behind (tab closed without signing
        // out, or a sign-out that failed to purge). Never let it be rendered
        // to this user.
        resetIdentityScopedState();
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      platformLogin: (user, token) => {
        resetIdentityScopedState();
        set({
          user: { ...user, isPlatformUser: true },
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: async () => {
        const state = get();
        try {
          if (state.user?.isPlatformUser) {
            await platformAuthService.logout();
          } else {
            await authService.logout();
          }
        } catch {
          // Even if logout API fails, clear local state
        } finally {
          // Clear platform tokens as well
          localStorage.removeItem('platformAccessToken');
          localStorage.removeItem('platformRefreshToken');
          // Drop every cached server response and tenant-scoped store, so the
          // next sign-in in this browser cannot be shown this session's data.
          resetIdentityScopedState();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      initialize: async () => {
        const state = get();

        // Wait for hydration to complete before checking auth state
        if (!state._hasHydrated) {
          return;
        }

        // Check for platform token first
        const hasPlatformToken = platformAuthService.isAuthenticated();
        if (hasPlatformToken) {
          try {
            const platformUser = await platformAuthService.getCurrentUser();
            set({
              user: {
                id: platformUser.id,
                email: platformUser.email,
                name: platformUser.displayName || platformUser.firstName || platformUser.email,
                role: platformUser.platformRole,
                tenantId: '',
                isPlatformUser: true,
                platformRole: platformUser.platformRole,
                isPrimary: !!platformUser.isPrimary,
              },
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          } catch {
            // Platform token invalid, clear it
            localStorage.removeItem('platformAccessToken');
            localStorage.removeItem('platformRefreshToken');
          }
        }

        // Check if we have a tenant token in localStorage (source of truth)
        const hasToken = authService.isAuthenticated();

        if (hasToken) {
          // Verify token is still valid by fetching current user
          try {
            const user = await authService.getCurrentUser();
            set((state) => ({
              user: {
                id: user.id,
                email: user.email,
                name: user.name,
                // The /auth/me payload doesn't always carry avatarUrl. When it's
                // absent, keep the avatar already in the store (persisted from
                // the last save) instead of clobbering it with undefined — that
                // overwrite is why the profile photo reverted on reload.
                avatarUrl: user.avatarUrl ?? state.user?.avatarUrl,
                role: user.role,
                tenantId: user.tenantId,
              },
              isAuthenticated: true,
              isLoading: false,
            }));
          } catch {
            // Token is invalid, clear state and stored tokens
            clearTokens();
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          // No token, ensure state is cleared
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'auth-storage',
      // Persist to localStorage, mirroring where the tokens are stored, so a
      // signed-in user survives a tab close.
      storage: createJSONStorage(() => authPersistStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called when hydration is complete
        state?.setHasHydrated(true);
      },
    }
  )
);
